import imageCompression from 'browser-image-compression';
import heic2any from 'heic2any';
import { uploadFileToAPI } from './observables';
import { db } from "./db";

/**
 * Uploads a pending file from IndexedDB to the API.
 * @param {string} uuid - The UUID of the file to be uploaded.
 * @returns {Promise<void>} - A promise that resolves when the upload is complete.
 */
export const uploadPendingFileFromIndexedDB = async (uuid) => {
  try {
    const upload = await db.upload.where({ uuid: uuid, status: 'pending' }).first();

    if (!upload) return;

    const compressedBlobAsFile = await compressImage(upload.file);
    await uploadFileToAPI(upload, compressedBlobAsFile);
  } catch (error) {
    console.error('Error uploading pending file from IndexedDB', error);
  }
}

/**
 * Compresses an image file using the specified compression options.
 * @param {File} file - The image file to compress.
 * @param {Object} compressionOptions - The compression options.
 * @param {number} compressionOptions.maxFilesize - The maximum file size in megabytes.
 * @param {boolean} compressionOptions.preserveExif - Whether to preserve EXIF data.
 * @param {number} compressionOptions.maxWidthOrHeight - The maximum width or height of the compressed image.
 * @returns {Promise<File>} A promise that resolves to the compressed image file.
 */
export async function compressImage(file, compressionOptions = {}) {
  if (file.type === 'image/heic' || file.type === 'image/heif') {
    const convertedBlob = await heic2any({
      blob: file,
      toType: 'image/jpeg',
      quality: 1
    });
    file = new File([convertedBlob], `${file.name}.jpg`, { type: 'image/jpeg' });
  }

  if (!file.type.startsWith('image/')) {
    console.error('The provided file is not an image.');
    return;
  }

  const options = {
    maxSizeMB: compressionOptions.maxFilesize || file.size > 20 ? 10 : 1,
    preserveExif: compressionOptions.preserveExif || true,
    maxWidthOrHeight: compressionOptions.maxWidthOrHeight || undefined,
    useWebWorker: true,
    alwaysKeepResolution: true,
    fileType: file.type === 'image/png' ? 'image/png' : 'image/jpeg',
  }

  const compressedBlob = await imageCompression(file, options);
  return new File([compressedBlob], file.name, { type: file.type });
}
